import { useCallback, useState } from "react";
import { supabase } from "@/lib/supabase";

export type AuthMode = "login" | "register";

export interface AuthValues {
  fullName: string;
  company: string;
  email: string;
  password: string;
}

export type AuthFieldErrors = Partial<Record<keyof AuthValues, string>>;

export interface AuthSubmitResult {
  ok: boolean;
  needsConfirm?: boolean;
}

const EMPTY_VALUES: AuthValues = {
  fullName: "",
  company: "",
  email: "",
  password: "",
};

function translateError(message: string): string {
  const msg = message.toLowerCase();
  if (msg.includes("invalid login credentials")) return "邮箱或密码不正确，请重新输入";
  if (msg.includes("email not confirmed")) return "邮箱尚未验证，请先前往邮箱完成验证";
  if (msg.includes("already registered") || msg.includes("already been registered")) {
    return "该邮箱已注册，请直接登录";
  }
  if (msg.includes("password") && msg.includes("at least")) return "密码至少需要 6 位";
  if (msg.includes("rate limit")) return "操作过于频繁，请稍后再试";
  return message;
}

export function useAuthForm() {
  const [mode, setMode] = useState<AuthMode>("login");
  const [values, setValues] = useState<AuthValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<AuthFieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const updateField = useCallback((key: keyof AuthValues, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setFormError(null);
  }, []);

  const switchMode = useCallback((next: AuthMode) => {
    setMode(next);
    setErrors({});
    setFormError(null);
  }, []);

  const validate = useCallback((): AuthFieldErrors => {
    const next: AuthFieldErrors = {};
    if (mode === "register" && !values.fullName.trim()) next.fullName = "请填写姓名";
    if (!values.email.trim()) next.email = "请填写邮箱";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) next.email = "邮箱格式不正确";
    if (!values.password) next.password = "请填写密码";
    else if (values.password.length < 6) next.password = "密码至少需要 6 位";
    return next;
  }, [mode, values]);

  const submit = useCallback(async (): Promise<AuthSubmitResult> => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return { ok: false };

    setSubmitting(true);
    setFormError(null);
    try {
      if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({
          email: values.email.trim(),
          password: values.password,
        });
        if (error) throw error;
        return { ok: true };
      }

      const { data, error } = await supabase.auth.signUp({
        email: values.email.trim(),
        password: values.password,
        options: {
          data: {
            full_name: values.fullName.trim(),
            company: values.company.trim(),
          },
        },
      });
      if (error) throw error;
      if (!data.session) {
        return { ok: true, needsConfirm: true };
      }
      return { ok: true };
    } catch (err) {
      setFormError(translateError(err instanceof Error ? err.message : "操作失败，请稍后重试"));
      return { ok: false };
    } finally {
      setSubmitting(false);
    }
  }, [mode, validate, values]);

  return { mode, values, errors, submitting, formError, updateField, switchMode, submit };
}